/**
 * File validation helpers for the service-request feature.
 */
import { ALLOWED_EXTENSIONS, MAX_FILE_BYTES } from '../constants';

export function getFileExtension(filename: string): string {
  return (filename.split('.').pop() ?? '').toLowerCase();
}

export function validateDocumentFile(file: File): string | null {
  if (!file) return 'No file selected';
  const ext = getFileExtension(file.name) as typeof ALLOWED_EXTENSIONS[number];
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `Unsupported format. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }
  if (file.size > MAX_FILE_BYTES) {
    return `File too large (max 20 MB, got ${(file.size / 1024 / 1024).toFixed(1)} MB)`;
  }
  return null;
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Save an already-fetched Blob to disk by clicking a temporary anchor.
 *
 * The download endpoint streams the file bytes (documents live on local disk,
 * not S3 — there's no presigned URL) and is Bearer-auth protected, so the file
 * MUST be fetched through the authenticated api client and handed off here as a
 * Blob. We deliberately do NOT `window.open` the endpoint: a new tab carries no
 * Authorization header, so it just opens a blank, unauthenticated page and
 * downloads nothing.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so the click has a chance to kick off the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
